import { css } from '@firebolt-dev/css'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Client } from '../client/Client'
import { BrowserServer } from '../server/BrowserServer'
import {
  BoxIcon,
  CastleIcon,
  CatIcon,
  CodeIcon,
  CopyIcon,
  EarthIcon,
  FilePlusCornerIcon,
  FolderClosedIcon,
  GemIcon,
  HamIcon,
  HammerIcon,
  HouseIcon,
  ImageIcon,
  LayoutListIcon,
  LoaderPinwheelIcon,
  Music2Icon,
  MusicIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  PencilIcon,
  PersonStandingIcon,
  PlusIcon,
  SearchIcon,
  SettingsIcon,
  ShirtIcon,
  Trash2Icon,
  UnplugIcon,
  XIcon,
} from 'lucide-react'
import { SocketLocal } from '../client/SocketLocal'
import { BrowserFS } from '../server/BrowserFS'
import { cloneDeep, times } from 'lodash-es'
import { Assets } from '../core/assets'
import { storage } from '../web/storage'
import { uuid } from '../core/utils'
import { cls } from '../core/cls'
import { SocketRemote } from '../client/SocketRemote'
import { UIEditorAssetPane } from './UIEditorAssetPane'
import { UIEditorScriptPane } from './UIEditorScriptPane'
import { FileArea } from './FileArea'
import { UIExplorer } from './UIExplorer'
import { UIInspector } from './UIInspector'

const editableAssets = ['block', 'script', 'body']
const inspectableAssets = ['sound']

export function UIEditor() {
  const [modal, setModal] = useState(null)
  const [client, setClient] = useState(null)
  const [server, setServer] = useState(null)

  function showModal(options) {
    return new Promise(resolve => {
      setModal({
        ...options,
        close(action) {
          setModal(null)
          resolve(action)
        },
      })
    })
  }

  return (
    <div
      css={css`
        --bg1: #0f0f14; // dark
        --bg2: #191920; // dark highlight
        --bg3: #1f1f28; // main bg
        --bg4: #2a2936; // main highlight
        --bg5: #3a3947; // main active

        position: absolute;
        top: 0;
        left: 0;
        width: 100dvw;
        height: 100dvh;
      `}
    >
      {!client && <UIEditorSplash setClient={setClient} setServer={setServer} showModal={showModal} />}
      {client && <UIEditorMain client={client} server={server} showModal={showModal} />}
      {modal && <Modal modal={modal} />}
    </div>
  )
}

export function UIEditorSplash({ setClient, setServer, showModal }) {
  const [creating, setCreating] = useState(false)
  const [opening, setOpening] = useState(false)

  async function create() {
    const dirHandle = await window.showDirectoryPicker({ mode: 'readwrite' })
    const fs = new BrowserFS({ dirHandle })
    const empty = await fs.isEmpty('/')
    if (!empty) {
      return showModal({
        title: 'Folder not empty',
        message: 'An empty folder is required to save new world files to.',
        btns: [
          {
            label: 'Okay',
            action: null,
          },
        ],
      })
    }
    setCreating(true)
    await scaffold(fs)
    const server = new BrowserServer({ dirHandle })
    let playerId = storage.get('playerId')
    if (!playerId) {
      playerId = uuid()
      storage.set('playerId', playerId)
    }
    const params = {
      playerId,
      mode: 'editor',
    }
    const socket = new SocketLocal(server, params)
    const client = new Client()
    await client.init({ socket })
    await client.network.connected
    setCreating(false)
    setServer(server)
    setClient(client)
  }

  async function open() {
    const dirHandle = await window.showDirectoryPicker({ mode: 'readwrite' })
    setOpening(true)
    const server = new BrowserServer({ dirHandle })
    let playerId = storage.get('playerId')
    if (!playerId) {
      playerId = uuid()
      storage.set('playerId', playerId)
    }
    const params = {
      playerId,
      mode: 'editor',
    }
    const socket = new SocketLocal(server, params)
    const client = new Client()
    await client.init({ socket })
    await client.network.connected
    setServer(server)
    setClient(client)
    setOpening(false)
  }

  async function connect() {
    let playerId = storage.get('playerId')
    if (!playerId) {
      playerId = uuid()
      storage.set('playerId', playerId)
    }
    playerId = playerId + '_editor'
    const params = {
      playerId,
      mode: 'editor',
    }
    const protocol = window.location.protocol.startsWith('https') ? 'wss' : 'ws'
    const wsUrl = `${protocol}://${window.location.host}/`
    const socket = new SocketRemote(wsUrl, params)
    const client = new Client()
    client.once('disconnect', () => setClient(null))
    await client.init({ socket })
    await client.network.connected
    setClient(client)
  }

  return (
    <div
      className='splash'
      css={css`
        .splash {
          position: absolute;
          top: 0;
          left: 0;
          width: 100dvw;
          height: 100dvh;
          z-index: 1;
          background: var(--bg1);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .splash-box {
          width: 320px;
          /* background: var(--bg2); */
          border-radius: 5px;
          padding: 30px;
        }
        .splash-btn {
          background: var(--bg3);
          border-radius: 5px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 10px;
          height: 44px;
          margin: 0 0 10px;
          &:last-child {
            margin-bottom: 0;
          }
          &:hover {
            cursor: pointer;
            background: var(--bg4);
          }
          > svg {
            &.spin {
              animation: spin 2s linear infinite;
              @keyframes spin {
                from {
                  transform: rotate(0turn);
                }
                to {
                  transform: rotate(1turn);
                }
              }
            }
          }
          > span {
            margin-left: 10px;
          }
        }
      `}
    >
      <div className='splash'>
        <div className='splash-box'>
          <div className='splash-btn' onClick={create}>
            {!creating && (
              <>
                <FilePlusCornerIcon size={20} />
                <span>New</span>
              </>
            )}
            {creating && <LoaderPinwheelIcon className='spin' size={18} />}
          </div>
          <div className='splash-btn' onClick={open}>
            {!opening && (
              <>
                <FolderClosedIcon size={20} />
                <span>Open</span>
              </>
            )}
            {opening && <LoaderPinwheelIcon className='spin' size={18} />}
          </div>
          <div className='splash-btn' onClick={connect}>
            <UnplugIcon size={20} />
            <span>Connect</span>
          </div>
        </div>
      </div>
    </div>
  )
}

const Icons = {
  home: HouseIcon,
  settings: SettingsIcon,
  world: EarthIcon,
  block: BoxIcon,
  item: GemIcon,
  tool: HammerIcon,
  consumable: HamIcon,
  wearable: ShirtIcon,
  entity: CatIcon,
  script: CodeIcon,
  structure: CastleIcon,
  sound: Music2Icon,
  body: PersonStandingIcon,
  texture: ImageIcon,
  icon: ImageIcon,
}

function Icon({ type, ...props }) {
  const IconClass = Icons[type]
  if (!IconClass) return null
  return <IconClass {...props} />
}

function UIEditorMain({ client, server, showModal }) {
  const [tabs, setTabs] = useState([]) // { id: uuid, icon, label, data: { asset, texture, icon }, save }
  const [tab, setTab] = useState({ id: 'world' })

  const tabRef = useRef()
  tabRef.current = tab

  const saveTab = async tab => {
    if (!tab.save) return
    const saved = await tab.save()
    return saved
  }

  useEffect(() => {
    function onKeyDown(e) {
      const isCtrl = e.metaKey || e.ctrlKey
      const isShift = e.shiftKey
      const key = e.key.toLowerCase()
      if (key === 's' && isCtrl && !isShift) {
        e.preventDefault()
        saveTab(tabRef.current)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [])

  return (
    <div
      className='editor'
      css={css`
        position: absolute;
        inset: 0;
        background: var(--bg1);
        padding: 10px;
        display: flex;
        align-items: stretch;
        gap: 10px;
        .editor-explorer {
          display: flex;
        }
        .editor-main {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .editor-head {
          display: flex;
          margin: 0 0 10px;
        }
        .editor-documents {
          flex: 1;
          position: relative;
        }
        .editor-tab {
          height: 40px;
          min-width: 40px;
          padding: 0 14px 0 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 5px;
          margin-right: 4px;
          &-label {
            margin-left: 6px;
          }
          &-omni {
            margin-left: 6px;
            margin-right: -6px;
            position: relative;
            > * {
              background: var(--bg1);
            }
            &:hover {
              .editor-tab-dirty {
                display: none;
              }
            }
          }
          &-close {
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #5b6371;
            &:hover {
              cursor: pointer;
              color: white;
            }
          }
          &-dirty {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--bg1);
            border-radius: 2px;
            > div {
              width: 7px;
              height: 7px;
              border-radius: 4px;
              background: white;
            }
          }
          &:hover {
            cursor: pointer;
          }
          &:hover:not(.active) {
            background: var(--bg2);
            .editor-tab-omni > * {
              background: var(--bg2);
            }
          }
          &.active {
            background: var(--bg3);
            .editor-tab-omni > * {
              background: var(--bg3);
            }
          }
        }
        .editor-document {
          position: absolute;
          inset: 0;
          display: none;
          &.visible {
            display: flex;
          }
        }
        .editor-inspect {
          width: 300px;
          background: var(--bg3);
          border-radius: 5px;
        }
      `}
    >
      <div className='editor-explorer'>
        <UIExplorer
          client={client}
          onNew={async info => {
            const asset = await createNewAsset(info)
            const isEditable = editableAssets.includes(asset.type)
            if (isEditable) {
              const tab = {
                id: uuid(),
                icon: null,
                label: null,
                data: {
                  asset,
                  // texture: null,
                  // icon: null,
                  isNew: true,
                },
                save: null,
              }
              setTabs(tabs => {
                return [...tabs, tab]
              })
              setTab(tab)
              return
            }
            // raw/inspectable file
            const ops = {
              upsert: [asset],
              remove: [],
            }
            client.network.send('editAssets', ops)
          }}
          onOpen={asset => {
            const isEditable = editableAssets.includes(asset.type)
            if (isEditable) {
              let tab = tabs.find(t => t.data?.asset?.id === asset.id)
              if (!tab) {
                tab = {
                  id: uuid(),
                  icon: null,
                  label: null,
                  data: {
                    asset,
                    isNew: false,
                  },
                  save: null,
                }
                // herenow
                if (asset.type === 'block' || asset.type === 'body') {
                  const registry = client.registry
                  const texture = registry.get('texture', asset.model.texture) || null
                  const icon = registry.get('icon', asset.icon) || null
                  tab.data.texture = texture
                  tab.data.icon = icon
                }
                setTabs(tabs => {
                  return [...tabs, tab]
                })
              }
              setTab(tab)
              return
            }
            const isInspectable = inspectableAssets.includes(asset.type)
            if (isInspectable) {
              return console.error('inspect should have been caught in UIExplorer?')
            }
          }}
          onDuplicate={asset => {
            const tab = {
              id: uuid(),
              icon: null,
              label: null,
              data: {
                asset,
                isNew: true,
              },
              save: null,
            }
            if (asset.type === 'block' || asset.type === 'body') {
              const registry = client.registry
              const texture = registry.get('texture', asset.model.texture) || null
              const icon = registry.get('icon', asset.icon) || null
              tab.data.texture = texture
              tab.data.icon = icon
            }
            setTabs(tabs => {
              return [...tabs, tab]
            })
            setTab(tab)
          }}
          onRemove={async asset => {
            const proceed = await showModal({
              title: 'Delete asset',
              message: 'Are you sure you want to delete this asset?',
              btns: [
                {
                  label: 'Delete',
                  action: true,
                },
                {
                  label: 'Cancel',
                  action: null,
                },
              ],
            })
            if (!proceed) return
            // remove tab if open
            const idx = tabs.findIndex(t => t.data.asset.type === asset.type && t.data.asset.id === asset.id)
            if (idx !== -1) {
              setTabs(tabs => {
                tabs = tabs.slice()
                tabs.splice(idx, 1)
                return tabs
              })
            }
            // send to server
            const ops = { remove: [{ type: asset.type, id: asset.id }] }
            client.network.send('editAssets', ops)
          }}
          showModal={showModal}
        />
      </div>
      <div className='editor-main'>
        <div className='editor-head'>
          <div
            className={cls('editor-tab', { active: tab?.id === 'world' })}
            onClick={() => {
              setTab({ id: 'world' })
            }}
          >
            <EarthIcon size={20} />
            <span className='editor-tab-label'>World</span>
          </div>
          {tabs.map((t, idx) => (
            <div
              key={idx}
              className={cls('editor-tab', { active: tab === t })}
              onClick={e => {
                if (e.defaultPrevented) return
                setTab(t)
              }}
            >
              <Icon type={t.icon} size={20} />
              <span className='editor-tab-label'>{t.label}</span>
              <div className='editor-tab-omni'>
                <div
                  className='editor-tab-close'
                  onClick={async e => {
                    e.preventDefault()
                    if (t.save) {
                      const action = await showModal({
                        title: 'Save changes',
                        message: 'Do you want to save your changes?',
                        btns: [
                          {
                            label: 'Save',
                            action: 'save',
                          },
                          {
                            label: 'Discard',
                            action: 'discard',
                          },
                          {
                            label: 'Cancel',
                            action: 'cancel',
                          },
                        ],
                      })
                      if (action === 'cancel') {
                        return false
                      }
                      if (action === 'save') {
                        const saved = await tab.save()
                        if (!saved) return false
                      }
                      if (action === 'discard') {
                        // no-op, continue
                      }
                    }
                    const idx = tabs.indexOf(t)
                    setTabs(tabs => {
                      tabs = tabs.slice()
                      tabs.splice(idx, 1)
                      if (tab === t) {
                        setTab(tabs[idx] || { id: 'world' })
                      }
                      return tabs
                    })
                  }}
                >
                  <XIcon size={18} />
                </div>
                {t.save && (
                  <div className='editor-tab-dirty'>
                    <div />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className='editor-documents'>
          <div className={cls('editor-document', { visible: tab.id === 'world' })}>
            <UIEditorWorld client={client} showModal={showModal} />
          </div>
          {tabs.map(t => (
            <div key={t.id} className={cls('editor-document', { visible: tab === t })}>
              {(t.data.asset.type === 'block' || t.data.asset.type === 'body') && (
                <UIEditorAssetPane
                  client={client}
                  tab={t}
                  editTab={fn => {
                    fn(t)
                    setTabs(tabs.slice())
                  }}
                  visible={tab === t}
                  showModal={showModal}
                />
              )}
              {t.data.asset.type === 'script' && (
                <UIEditorScriptPane
                  client={client}
                  tab={t}
                  editTab={fn => {
                    fn(t)
                    setTabs(tabs.slice())
                  }}
                  visible={tab === t}
                  showModal={showModal}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* <div className='editor-overlay'>
        <div className='editor-panes'>
          {tab?.id === 'menu' && (
            <div className='editor-pane visible'>
              <AssetsPane
                client={client}
                onCreate={async info => {
                  const asset = await createNewAsset(info)
                  const isEditable = editableAssets.includes(asset.type)
                  if (isEditable) {
                    const tab = {
                      id: uuid(),
                      icon: null,
                      label: null,
                      data: {
                        asset,
                        // texture: null,
                        // icon: null,
                        isNew: true,
                      },
                      save: null,
                    }
                    setTabs(tabs => {
                      return [...tabs, tab]
                    })
                    setTab(tab)
                    return
                  }
                  // raw file
                  const ops = {
                    upsert: [asset],
                    remove: [],
                  }
                  console.log('gooh', ops)
                  client.network.send('editAssets', ops)
                }}
                
              />
            </div>
          )}
        </div>
      </div> */}
    </div>
  )
}

// function AssetsPane({ client, onCreate, onSelect, onDuplicate, onRemove }) {
//   const assetsCreateRef = useRef()
//   const [query, setQuery] = useState('')
//   const [create, setCreate] = useState(false)
//   const [registryV, setRegistryV] = useState(0)

//   const registry = client.registry

//   useEffect(() => {
//     const onRegistryChange = () => {
//       setRegistryV(n => n + 1)
//     }
//     registry.on('change', onRegistryChange)
//     return () => {
//       registry.off('change', onRegistryChange)
//     }
//   }, [])

//   const assets = useMemo(() => {
//     const ignored = new Set(['texture', 'icon', 'air', 'unknown'])
//     const q = query.toLowerCase()
//     const assets = registry.raw.filter(asset => {
//       if (ignored.has(asset.type) || ignored.has(asset.id)) return false
//       return asset.id.toLowerCase().includes(q) || asset.type.toLowerCase().includes(q)
//     })
//     assets.sort((a, b) => a.id.localeCompare(b.id)) // name asc
//     return assets
//   }, [query, registryV])

//   console.log('assets', assets)

//   return (
//     <div
//       className='assets'
//       css={css`
//         pointer-events: auto;
//         position: absolute;
//         top: 0;
//         left: 0;
//         bottom: 0;
//         width: 320px;
//         background: var(--bg3);
//         display: flex;
//         flex-direction: column;
//         .assets-head {
//           padding: 16px;
//           display: flex;
//           align-items: center;
//         }
//         .assets-search {
//           flex: 1;
//           background: var(--bg4);
//           border-radius: 10px;
//           padding: 0 8px;
//           display: flex;
//           align-items: center;
//           svg {
//             margin-right: 6px;
//           }
//           input {
//             flex: 1;
//             width: 100%;
//             height: 34px;
//           }
//           &.focus {
//             background: var(--bg3);
//           }
//         }
//         .assets-create {
//           margin-left: 8px;
//           position: relative;
//           &-btn {
//             height: 34px;
//             padding: 0 16px;
//             border-radius: 10px;
//             background: var(--bg4);
//             display: flex;
//             align-items: center;
//             &:hover {
//               cursor: pointer;
//             }
//           }
//           &-menu {
//             display: none;
//             position: absolute;
//             top: calc(100% + 2px);
//             right: 0;
//             background: var(--bg4);
//             border-radius: 10px;
//             z-index: 1;
//             padding: 4px 0;
//             overflow: hidden;
//           }
//           &-menu-item {
//             display: flex;
//             align-items: center;
//             padding: 8px 19px 8px 16px;
//             position: relative;
//             overflow: hidden;
//             svg {
//               margin-right: 8px;
//             }
//             &:hover {
//               cursor: pointer;
//               background: var(--bg5);
//             }
//           }
//           &:focus {
//             .assets-create-btn {
//               background: var(--bg5);
//             }
//             .assets-create-menu {
//               display: block;
//             }
//           }
//         }
//         .assets-scroll {
//           flex: 1;
//           overflow-y: auto;
//         }
//         .assets-item {
//           display: flex;
//           align-items: center;
//           padding: 5px 16px;
//           &-icon {
//             width: 50px;
//             height: 50px;
//             border-radius: 10px;
//             background-size: cover;
//             background-position: center;
//             background-repeat: no-repeat;
//             background-color: var(--bg2);
//             margin-right: 10px;
//           }
//           &-info {
//             flex: 1;
//           }
//           &-id {
//             flex: 1;
//             white-space: nowrap;
//             text-overflow: ellipsis;
//             overflow: hidden;
//             margin: 0 0 3px;
//           }
//           &-type {
//             color: #5b6371;
//             display: flex;
//             align-items: center;
//             svg {
//               margin-right: 3px;
//             }
//             span {
//               font-size: 14px;
//             }
//           }
//           &-btns {
//             display: flex;
//             align-items: center;
//           }
//           &-btn {
//             width: 20px;
//             height: 20px;
//             display: flex;
//             align-items: center;
//             justify-content: center;
//             margin-left: 8px;
//             color: #4f4f66;
//             &:hover {
//               cursor: pointer;
//               color: white;
//             }
//           }
//           &:hover {
//             cursor: pointer;
//             background: var(--bg4);
//           }
//         }
//       `}
//     >
//       <div className='assets-head'>
//         <div className='assets-search'>
//           <SearchIcon size={20} />
//           <input type='text' value={query} onChange={e => setQuery(e.target.value)} placeholder='Search assets' />
//         </div>
//         <div
//           className='assets-create'
//           ref={assetsCreateRef}
//           tabIndex={0}
//           onFocus={() => setCreate(true)}
//           onBlur={() => setCreate(false)}
//         >
//           <div
//             className='assets-create-btn'
//             onClick={e => {
//               // e.preventDefault()
//               // e.stopPropagation()
//               // if (document.activeElement === assetsCreateRef.current) {
//               //   assetsCreateRef.current.blur()
//               // } else {
//               //   assetsCreateRef.current.focus()
//               // }
//             }}
//           >
//             <PlusIcon size={20} />
//           </div>
//           <div className='assets-create-menu'>
//             <div
//               className='assets-create-menu-item'
//               onClick={() => {
//                 assetsCreateRef.current.blur()
//                 onCreate({ type: 'block' })
//               }}
//             >
//               <Icon type='block' size={20} />
//               <span>Block</span>
//             </div>
//             <div
//               className='assets-create-menu-item'
//               onClick={() => {
//                 assetsCreateRef.current.blur()
//                 onCreate({ type: 'script' })
//               }}
//             >
//               <Icon type='script' size={20} />
//               <span>Script</span>
//             </div>
//             <label
//               className='assets-create-menu-item'
//               onFocus={() => {
//                 assetsCreateRef.current.focus()
//               }}
//             >
//               <Icon type='sound' size={20} />
//               <span>Sound</span>
//               <FileArea
//                 accept='audio/mp3'
//                 onFile={file => {
//                   console.log('bruh')
//                   assetsCreateRef.current.blur()
//                   onCreate({ type: 'sound', file })
//                 }}
//               />
//             </label>
//           </div>
//         </div>
//       </div>
//       <div className='assets-scroll noscrollbar'>
//         {assets.map(asset => (
//           <div
//             key={`${asset.type}.${asset.id}`}
//             className='assets-item'
//             onClick={e => {
//               if (e.defaultPrevented) return
//               onSelect(asset)
//             }}
//           >
//             <div
//               className='assets-item-icon'
//               css={css`
//                 background-image: url(${registry.icons.byId[asset.icon]?.url});
//               `}
//             />
//             <div className='assets-item-info'>
//               <div className='assets-item-id'>{asset.id}</div>
//               <div className='assets-item-type'>
//                 <Icon type={asset.type} size={14} />
//                 <span>{Assets[asset.type].label}</span>
//               </div>
//             </div>
//             <div className='assets-item-btns'>
//               <div
//                 className='assets-item-btn'
//                 onClick={e => {
//                   e.preventDefault()
//                   onDuplicate(asset)
//                 }}
//               >
//                 <CopyIcon size={16} />
//               </div>
//               <div
//                 className='assets-item-btn'
//                 onClick={e => {
//                   e.preventDefault()
//                   onRemove(asset)
//                 }}
//               >
//                 <Trash2Icon size={16} />
//               </div>
//             </div>
//           </div>
//         ))}
//       </div>
//     </div>
//   )
// }

function UIEditorWorld({ client, showModal }) {
  const viewportRef = useRef()
  const player = client.entities.player
  const registry = client.registry
  const [blocks, setBlocks] = useState(() => player.data.blocks)
  const [blockIdx, setBlockIdx] = useState(() => player.data.blockIdx)
  const [registryV, setRegistryV] = useState(0)
  const [blockPickerIdx, setBlockPickerIdx] = useState(null)

  const blockBarItems = useMemo(() => {
    const registry = client.registry
    return blocks.map(id => {
      const asset = registry.get('block', id)
      return {
        id,
        icon: registry.get('icon', asset?.icon)?.url,
      }
    })
  }, [blocks, registryV])

  useEffect(() => {
    const onRegistryChange = () => {
      setRegistryV(n => n + 1)
    }
    const onBlocksChange = blocks => {
      setBlocks(blocks.slice())
    }
    registry.on('change', onRegistryChange)
    // player.on('pointerlock', onPointerLockChange)
    player.on('blocks', onBlocksChange)
    player.on('blockIdx', setBlockIdx)
    return () => {
      registry.off('change', onRegistryChange)
      // player.off('pointerlock', onPointerLockChange)
      player.off('blocks', onBlocksChange)
      player.off('blockIdx', setBlockIdx)
    }
  }, [])

  useEffect(() => {
    const viewport = viewportRef.current
    client.graphics.setViewport(viewport)
  }, [])

  return (
    <div
      className='main'
      css={css`
        position: absolute;
        inset: 0;
        .world-viewport {
          position: absolute;
          inset: 0;
          canvas {
            border-radius: 5px;
          }
        }
        .world-blocks {
          position: absolute;
          bottom: 10px;
          left: 10px;
          right: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          &-bar {
            pointer-events: auto;
            background: var(--bg3);
            border-radius: 5px;
            padding: 5px;
            display: flex;
          }
          &-item {
            position: relative;
            width: 60px;
            height: 60px;
            border: 1.5px solid transparent;
            border-radius: 5px;
            background-color: var(--bg2);
            background-position: center;
            background-size: cover;
            background-repeat: no-repeat;
            margin-right: 5px;
            &.active {
              border: 1.5px solid rgba(255, 255, 255, 0.6);
            }
            &:last-child {
              margin-right: 0;
            }
          }
          &-num {
            position: absolute;
            top: 3px;
            left: 3px;
            font-size: 10px;
            font-weight: 500;
            line-height: 1;
          }
          &-edit {
            position: absolute;
            top: 0;
            right: 0;
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            display: none;
            &:hover {
              cursor: pointer;
            }
          }
          &-item:hover {
            .world-blocks-edit {
              display: flex;
            }
          }
        }
        top: 0;
        left: 0;
        right: 0;
      `}
    >
      <div className='world-viewport' ref={viewportRef} />
      <Crosshair />
      <div className='world-blocks'>
        <div className='world-blocks-bar'>
          {blockBarItems.map((item, idx) => (
            <div
              key={idx}
              className={cls('world-blocks-item', { active: blockIdx === idx })}
              css={css`
                background-image: url(${item.icon});
              `}
              onClick={e => {
                if (e.defaultPrevented) return
                player.setBlockIdx(idx, true)
              }}
            >
              <div className='world-blocks-num'>{idx + 1}</div>
              <div
                className='world-blocks-edit'
                onClick={e => {
                  e.preventDefault()
                  setBlockPickerIdx(idx)
                }}
              >
                <PencilIcon size={12} />
              </div>
            </div>
          ))}
        </div>
      </div>
      {blockPickerIdx !== null && (
        <BlockPicker
          registry={registry}
          onSelect={blockId => {
            player.setBlockAtIdx(blockPickerIdx, blockId, true)
            setBlockPickerIdx(null)
          }}
          onClose={() => setBlockPickerIdx(null)}
        />
      )}
    </div>
  )
}

function BlockPicker({ registry, onSelect, onClose }) {
  const items = useMemo(() => {
    const items = []
    for (const asset of registry.raw) {
      if (asset.type === 'block') {
        if (asset.id === 'air') continue
        if (asset.id === 'unknown') continue
        items.push({
          id: asset.id,
          icon: registry.get('icon', asset.icon)?.url,
        })
      }
    }
    items.sort((a, b) => a.id.localeCompare(b.id)) // name asc
    console.log('items', items)
    return items
  }, [])
  return (
    <div
      className='blockpicker'
      css={css`
        position: absolute;
        left: 0;
        right: 0;
        bottom: 94px;
        display: flex;
        align-items: center;
        justify-content: center;
        .blockpicker-box {
          width: 395px;
          height: 360px;
          background: var(--bg4);
          border-radius: 10px;
          overflow-y: auto;
        }
        .blockpicker-items {
          display: flex;
          flex-wrap: wrap;
          gap: 5px;
          padding: 5px;
        }
        .blockpicker-item {
          width: 60px;
          cursor: pointer;
        }
        .blockpicker-icon {
          padding-bottom: 100%;
          border-radius: 10px;
          background-color: var(--bg3);
          background-size: cover;
          background-repeat: no-repeat;
        }
      `}
      onClick={e => {
        if (e.defaultPrevented) return
        onClose()
      }}
    >
      <div className='blockpicker-box noscrollbar' onClick={e => e.preventDefault()}>
        <div className='blockpicker-items'>
          {items.map(item => (
            <div key={item.id} className='blockpicker-item' onClick={() => onSelect(item.id)}>
              <div
                className='blockpicker-icon'
                css={css`
                  background-image: url(${item.icon});
                `}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function Crosshair() {
  return (
    <div
      className='crosshair'
      css={css`
        pointer-events: none;
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        mix-blend-mode: difference;
        svg {
        }
      `}
    >
      <svg width='32' height='32' viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <rect x='13' y='11' width='6' height='2' fill='white' />
        <rect x='13' y='19' width='6' height='2' fill='white' />
        <rect x='19' y='13' width='2' height='6' fill='white' />
        <rect x='11' y='13' width='2' height='6' fill='white' />
      </svg>
    </div>
  )
}

function Modal({ modal }) {
  useEffect(() => {
    function onKeyDown(e) {
      if (e.code === 'Escape') {
        modal.close(null)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [])
  return (
    <div
      className='studio-modal'
      css={css`
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.4);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2;
        .studio-modal-box {
          background: var(--bg3);
          border-radius: 10px;
          max-width: 320px;
        }
        .studio-modal-body {
          padding: 20px;
        }
        .studio-modal-title {
          font-weight: 600;
          margin: 0 0 10px;
        }
        .studio-modal-message {
          line-height: 1.4;
          color: rgba(255, 255, 255, 0.6);
          margin: 0 0 10px;
        }
        .studio-modal-btns {
          border-top: 1px solid var(--bg4);
          border-bottom-left-radius: 10px;
          border-bottom-right-radius: 10px;
          overflow: hidden;
          display: flex;
        }
        .studio-modal-btn {
          flex: 1;
          height: 44px;
          padding: 0 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-right: 1px solid var(--bg4);
          &:last-child {
            border-right: 0;
          }
          &:hover {
            cursor: pointer;
            background: var(--bg4);
          }
        }
      `}
    >
      <div className='studio-modal-box'>
        <div className='studio-modal-body'>
          <div className='studio-modal-title'>{modal.title}</div>
          <div className='studio-modal-message'>{modal.message}</div>
        </div>
        <div className='studio-modal-btns'>
          {modal.btns.map(btn => (
            <div key={btn.label} className='studio-modal-btn' onClick={() => modal.close(btn.action)}>
              <span>{btn.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

async function fetchFile(url) {
  const resp = await fetch(url)
  const blob = await resp.blob()
  const filename = url.split('/').pop()
  const file = new File([blob], filename, { type: blob.type })
  return file
}

async function fetchJSON(url) {
  const resp = await fetch(url)
  const data = await resp.text()
  return JSON.parse(data)
}

async function scaffold(fs) {
  const manifest = await fetchJSON(`/resources/template/manifest.json`)
  const promises = []
  for (const filePath of manifest) {
    promises.push(
      new Promise(async resolve => {
        const file = await fetchFile(`/resources/template/${filePath}`)
        await fs.insertFile(filePath, file)
        resolve()
      })
    )
  }
  await Promise.all(promises)
}

async function createNewAsset(info) {
  if (info.type === 'block') {
    return {
      id: 'block',
      type: 'block',
      name: '',
      tags: [],
      model: {
        root: {
          id: '$root',
          type: 'group',
          position: [0, 0, 0],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
          conditions: [],
          children: [
            {
              id: 'box',
              type: 'box',
              position: [0, 16, 0],
              rotation: [0, 0, 0],
              scale: [1, 1, 1],
              conditions: [],
              children: [],
              size: [32, 32, 32],
              // prettier-ignore
              faces: {
                up: { x:0, y:0, rotation:0, density:1, flipX:false, flipY:false },  
                down: { x:0, y:0, rotation:0, density:1, flipX:false, flipY:false }, 
                north: { x:0, y:0, rotation:0, density:1, flipX:false, flipY:false }, 
                east: { x:0, y:0, rotation:0, density:1, flipX:false, flipY:false }, 
                south: { x:0, y:0, rotation:0, density:1, flipX:false, flipY:false }, 
                west: { x:0, y:0, rotation:0, density:1, flipX:false, flipY:false },
              },
            },
            {
              id: 'hitbox',
              type: 'hitbox',
              position: [0, 16, 0],
              conditions: [],
              children: [],
              size: [32, 32, 32],
            },
            {
              id: 'collider',
              type: 'collider',
              position: [0, 16, 0],
              rotation: [0, 0, 0],
              scale: [1, 1, 1],
              conditions: [],
              children: [],
              size: [32, 32, 32],
            },
          ],
        },
        texture: null,
      },
      pose: {
        icon: { position: [0, 0, 0], rotation: [30, 45, 0], scale: [1, 1, 1] },
        socket: { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
        hand: { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
        ground: { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
      },
      solid: [true, true, true, true, true, true],
      opaque: [true, true, true, true, true, true],
      attenuation: 1,
      light: [0, 0, 0],
      replaceable: false,
      dimming: true,
      behaviors: {},
    }
  }
  if (info.type === 'script') {
    return {
      id: 'script',
      type: 'script',
      code: '// ...',
    }
  }
  if (info.type === 'audio') {
    return {
      id: info.file.name.split('.')[0],
      type: 'audio',
      data: new Uint8Array(await info.file.arrayBuffer()),
    }
  }
  if (info.type === 'sound') {
    return {
      id: 'sound',
      type: 'sound',
      spatial: true,
      volumeRandom: 0,
      pitchRandom: 0,
      variants: [{ id: null, volume: 1, pitch: 1, weight: 1 }],
    }
  }
  if (info.type === 'texture') {
    return {
      id: info.file.name.split('.')[0],
      type: 'texture',
      data: new Uint8Array(await info.file.arrayBuffer()),
    }
  }
}
